/**
 * Jurisdiction filtering for assets (aligned with mobile `DownloadScreen`):
 * user registration may list counties; assets expose `assetLocations` with county/state pairs.
 */

/** Same key shape as legacy mobile: `${county.trim()} - ${state.trim()}`. */
export function jurisdictionCountyStateKey(county: string, state: string): string {
  return `${county.trim()} - ${state.trim()}`;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

function coerceGeoToken(v: unknown): string | null {
  if (typeof v === "string" && v.trim()) return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;
}

/**
 * County/state from a jurisdiction row or `assetLocations` row (PascalCase + nested shapes).
 */
export function countyStateFromLocationLikeRow(o: Record<string, unknown>): {
  county: string;
  state: string;
} | null {
  const nestedCounty = o.county ?? o.County;
  const directState = o.state ?? o.State;

  if (nestedCounty && typeof nestedCounty === "object") {
    const nc = nestedCounty as Record<string, unknown>;
    const c = coerceGeoToken(nc.county ?? nc.County ?? nc.name ?? nc.Name ?? nc.displayValue ?? nc.DisplayValue);
    const st = coerceGeoToken(nc.state ?? nc.State ?? directState);
    if (c && st) return { county: c, state: st };
  }

  const county =
    (typeof nestedCounty === "string" || typeof nestedCounty === "number"
      ? coerceGeoToken(nestedCounty)
      : null) ?? coerceGeoToken(o.name ?? o.Name ?? o.displayValue ?? o.DisplayValue);
  const state = coerceGeoToken(directState);
  if (county && state) return { county, state };
  return null;
}

function registrationChild(data: Record<string, unknown>): Record<string, unknown> | null {
  const nested = data.registration ?? data.Registration;
  return nested && typeof nested === "object" ? (nested as Record<string, unknown>) : null;
}

function jurisdictionArrayOn(o: Record<string, unknown> | null): unknown[] | null {
  if (!o) return null;
  const v = o.jurisdiction ?? o.Jurisdiction;
  return Array.isArray(v) ? v : null;
}

/**
 * Jurisdiction list may live on `data` or on `data.registration` (or both). Prefer a non-empty array.
 */
function pickJurisdictionArray(
  data: Record<string, unknown>,
  reg: Record<string, unknown> | null,
): unknown[] | null {
  const onData = jurisdictionArrayOn(data);
  const onReg = jurisdictionArrayOn(reg);
  if (onData && onData.length > 0) return onData;
  if (onReg && onReg.length > 0) return onReg;
  if (onData) return onData;
  if (onReg) return onReg;
  return null;
}

function operatorAllJurisdictionsFlag(data: Record<string, unknown>, reg: Record<string, unknown> | null): boolean {
  if (data.operatorAllJurisdictions === true || data.OperatorAllJurisdictions === true) return true;
  if (reg?.operatorAllJurisdictions === true || reg?.OperatorAllJurisdictions === true) return true;
  return false;
}

export type ProfileJurisdictionParse =
  /** Persist these keys; user is geographically restricted. */
  | { kind: "restricted"; keys: ReadonlySet<string> }
  /** Explicitly no geographic restriction — safe to clear stored keys. */
  | { kind: "unrestricted" }
  /** Could not read jurisdiction from payload — do not clear existing storage. */
  | { kind: "indeterminate" };

/**
 * Interpret get-registration-profile JSON for jurisdiction storage and in-app filtering.
 * `indeterminate` means "leave localStorage alone" (avoids wiping keys on parse drift).
 */
export function parseProfileJurisdiction(json: unknown): ProfileJurisdictionParse {
  const root = asRecord(json);
  if (!root) return { kind: "indeterminate" };

  const data = asRecord(root.data) ?? root;
  const reg = registrationChild(data);

  if (operatorAllJurisdictionsFlag(data, reg)) return { kind: "unrestricted" };

  const jur = pickJurisdictionArray(data, reg);
  if (jur === null) return { kind: "indeterminate" };

  if (jur.length === 0) return { kind: "unrestricted" };

  const first = jur[0];
  if (first && typeof first === "object") {
    const fr = first as Record<string, unknown>;
    const id = fr.id ?? fr.Id;
    if (typeof id === "string" && id.trim().toLowerCase() === "all") return { kind: "unrestricted" };
  }

  const keys = new Set<string>();
  for (const row of jur) {
    if (!row || typeof row !== "object") continue;
    const cs = countyStateFromLocationLikeRow(row as Record<string, unknown>);
    if (cs) keys.add(jurisdictionCountyStateKey(cs.county, cs.state));
  }

  if (keys.size > 0) return { kind: "restricted", keys };

  /** Rows present but none parsed — do not treat as unrestricted (would wipe storage). */
  return { kind: "indeterminate" };
}

/**
 * Parse `get-registration-profile` (or equivalent) JSON into a set of county–state keys.
 * Returns `null` when unrestricted or when jurisdiction could not be determined.
 */
export function jurisdictionCountyStateKeysFromProfilePayload(json: unknown): ReadonlySet<string> | null {
  const r = parseProfileJurisdiction(json);
  return r.kind === "restricted" ? r.keys : null;
}

/**
 * Parse JSON persisted in `localStorage` (string keys, profile-shaped object, or `{ jurisdiction: [] }`).
 */
export function jurisdictionCountyStateKeysFromStoredJson(parsed: unknown): ReadonlySet<string> | null {
  if (parsed == null) return null;

  const fromProfile = jurisdictionCountyStateKeysFromProfilePayload(parsed);
  if (fromProfile && fromProfile.size > 0) return fromProfile;

  if (Array.isArray(parsed)) {
    const set = new Set<string>();
    for (const x of parsed) {
      if (typeof x === "string" && x.trim()) set.add(x.trim());
      else if (x && typeof x === "object") {
        const cs = countyStateFromLocationLikeRow(x as Record<string, unknown>);
        if (cs) set.add(jurisdictionCountyStateKey(cs.county, cs.state));
      }
    }
    return set.size > 0 ? set : null;
  }

  if (typeof parsed === "object") {
    const o = parsed as Record<string, unknown>;
    const jur = o.jurisdiction ?? o.Jurisdiction;
    if (Array.isArray(jur)) return jurisdictionCountyStateKeysFromStoredJson(jur);

    const set = new Set<string>();
    for (const k of Object.keys(o)) {
      const t = k.trim();
      if (t.includes(" - ")) set.add(t);
    }
    return set.size > 0 ? set : null;
  }

  return null;
}
